import {
  Button,
  Flex,
  Image,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Input,
} from '@chakra-ui/react';
import { Predictions } from '@/model/predictions';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutPredictionsResultMutation } from '@/services/queries/predictions';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PredictionsResultModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Predictions;
  onClose: () => void;
  onDone?: () => void;
}

export const PredictionsResultModal = ({ isOpen, onClose, onDone, item }: PredictionsResultModalProps) => {
  const { t } = useTranslation();
  const [resultA, setResultA] = useState('');
  const [resultB, setResultB] = useState('');
  const putMutation = usePutPredictionsResultMutation();
  const toast = useToast();

  const handleConfirm = () => {
    const payload = {
      option_a_result: Number(resultA),
      option_b_result: Number(resultB),
    };

    putMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_predictions.components.predictions_result_modal.predictions_result_success'),
            status: 'success',
          });

          onDone?.();
        },

        onError: (e: any) => {
          toast({
            title: e?.message || t('pages.application_predictions.components.predictions_result_modal.predictions_result_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_predictions.components.predictions_result_modal.predictions_result_title')}
          </ModalHeader>
          <ModalBody>
            <Flex alignItems='center' justifyContent='center' gap={{ base: 4, md: 8 }}>
              <Flex direction='column' alignItems='center' gap={3}>
                <Image src={item.option_a_image_url} w='48px' h='48px' objectFit='contain' />
                <Text fontWeight='medium'>{item.option_a_name}</Text>
                <Input
                  type='number'
                  w='54px'
                  textAlign='center'
                  value={resultA}
                  onChange={event => setResultA(event.target.value)}
                />
              </Flex>

              <ChakraFeatherIcon name='x' w='40px' h='40px' color='brand.500' />

              <Flex direction='column' alignItems='center' gap={3}>
                <Image src={item.option_b_image_url} w='48px' h='48px' objectFit='contain' />
                <Text fontWeight='medium'>{item.option_b_name}</Text>
                <Input
                  type='number'
                  w='54px'
                  textAlign='center'
                  value={resultB}
                  onChange={event => setResultB(event.target.value)}
                />
              </Flex>
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Button variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_predictions.components.predictions_result_modal.predictions_result_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_predictions.components.predictions_result_modal.predictions_result_confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={putMutation.isLoading} />
    </>
  );
};
